import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card } from "../../../design-v2";
import { buildLoginRecoveryPath } from "../../../auth/returnPath";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";

export const SIGN_IN_TO_SYNC = "Sign in to sync";
const UNREACHABLE = "We couldn't reach Lyft Zone. Your workout stays saved on this device.";

/**
 * What "Sign in to sync" does once the Session has been re-checked (§11.4):
 *   "resume" - `userId` is authenticated again: stay in TRAIN, sync resumes;
 *   "switch" - another user is authenticated: TRAIN transitions to them (§11.5);
 *   "login"  - anonymous: go to /login with the current TRAIN path as `next`;
 *   "stay"   - the Session could not be resolved: remain local, nothing lost.
 */
export function signInToSyncOutcome(resolution, userId) {
  if (resolution?.status === "anonymous") return "login";
  if (resolution?.status === "authenticated" && resolution.user?.id) {
    return resolution.user.id === userId ? "resume" : "switch";
  }
  return "stay";
}

// The action beside the informational SyncStatusChip (H8-7): the chip never
// becomes the auth button. It re-checks the Session before any navigation, so
// a stale "authenticated" bootstrap can never bounce /login back (H8-1).
export default function SyncAuthNotice({ userId, visible }) {
  const bootstrap = useAuthBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!visible) return null;

  async function signIn() {
    if (checking) return;
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    setChecking(true);
    setUnreachable(false);
    let resolution = null;
    try {
      resolution = await bootstrap.reresolveSession?.();
    } catch (_error) {
      resolution = null;
    }
    // "resume" and "switch" need nothing more here: the bootstrap already
    // committed the user, the sync engine resumes for them, and TRAIN keeps or
    // remounts its subtree accordingly.
    const outcome = signInToSyncOutcome(resolution, userId);
    if (outcome === "login") {
      navigate(buildLoginRecoveryPath(returnPath));
      return;
    }
    if (!mountedRef.current) return;
    setChecking(false);
    setUnreachable(outcome === "stay");
  }

  return (
    <Card tone="muted" className="lz-workout-sync-notice lz-workout-sync-auth-notice">
      <div role="status">
        <p>{unreachable ? UNREACHABLE : "This workout is saved on this device. Sign in to sync it."}</p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        isLoading={checking}
        loadingLabel="Checking"
        onClick={signIn}
      >
        {SIGN_IN_TO_SYNC}
      </Button>
    </Card>
  );
}
